/**
 * Defer Planning Form While Annexes Open
 *
 * Le formulaire « Annexer le planning » (item 2 de la modale des annexes du CR)
 * n'est jamais remplacé pendant que l'architecte s'en sert (PR #731, revue #4).
 *
 * Le chokepoint `meeting_reports/attachments/_section_streams` remplace ce
 * formulaire à chaque rafraîchissement de la section 9, y compris ceux qui
 * n'ont AUCUN geste derrière eux : la diffusion du crochet d'annexe
 * d'`Exports::GenerateJob` et le rattrapage `exports#show`. Modale OUVERTE, le
 * remplacement écrasait le format, la couleur et les cases en cours de choix, et
 * repliait « Options d'export » (un `<details>` rendu fermé au repos).
 *
 * Le geste, ciblé : sur `turbo:before-stream-render` d'une action `replace` dont
 * la cible est EXACTEMENT `PLANNING_FORM_ID`, on enveloppe `event.detail.render`.
 * AU RENDU (Turbo 8 attend une frame après le dispatch), si la modale
 * `ANNEXES_MODAL_ID` est ouverte (pas `.hidden`), le rendu est MIS EN ATTENTE au
 * lieu d'être joué, et appliqué à la fermeture de la modale. Une seule attente :
 * la plus récente remplace la précédente, qui ne sera jamais jouée. Toute autre
 * cible, toute autre action, et ce même `replace` modale fermée passent tels
 * quels.
 *
 * La fermeture se lit de DEUX façons, la première qui arrive gagne :
 *   • `modal:close`, que `modal_controller.js#close` émet sur la racine de la
 *     modale, SANS bulle, au DÉBUT de sa fermeture (l'écouteur est donc posé sur
 *     la racine elle-même, jamais sur `document`) ;
 *   • la classe `hidden` posée sur cette racine, par le timer de 200 ms de
 *     `close()` ou instantanément par `window.switchModal`, qui n'émet rien.
 *     Sans ce second signal, une diffusion arrivée PENDANT l'animation de
 *     fermeture (après `modal:close`, avant `.hidden`) resterait en attente
 *     jusqu'à la fermeture SUIVANTE, et la modale rouverte montrerait un
 *     formulaire périmé.
 *
 * Les écritures web qui ferment la modale (dépôt, référence) ne changent pas de
 * comportement : leur réponse rend le `replace` AVANT l'`append` du
 * `closeModal`, donc le `replace` est mis en attente, puis appliqué au
 * `modal:close` que ce même `closeModal` émet, dans la même réponse.
 *
 * Les deux identifiants recopient `MeetingReports::AttachmentsComponent::
 * PLANNING_FORM_DOM_ID` et `MeetingReports::AnnexesModalComponent::MODAL_ID` ; un
 * test Ruby de parité lit ce fichier.
 *
 * @example
 * import { deferPlanningFormWhileAnnexesOpen } from './utils/defer_planning_form_while_annexes_open';
 * deferPlanningFormWhileAnnexesOpen();
 */

export const PLANNING_FORM_ID = 'meeting_report_annexes_planning_form';
export const ANNEXES_MODAL_ID = 'cr-annexes-modal';

const INSTALLED = Symbol.for('jerico.deferPlanningFormWhileAnnexesOpen');

/**
 * @typedef {object} DeferState
 * @property {(() => Promise<void>)=} render le rendu en attente, le plus récent
 * @property {Element=} modal la racine de modale sur laquelle l'attente est armée
 * @property {() => void=} onClose écouteur `modal:close` armé
 * @property {MutationObserver=} observer observateur de la classe `hidden`
 */

/**
 * @param {Element|null} modal
 * @returns {boolean}
 */
function isOpen(modal) {
  return Boolean(modal) && modal.isConnected && !modal.classList.contains('hidden');
}

/**
 * Retire les écouteurs de l'attente, sans la jouer.
 *
 * @param {DeferState} state
 */
function disarm(state) {
  state.modal?.removeEventListener('modal:close', state.onClose);
  state.observer?.disconnect();
  state.modal = undefined;
  state.onClose = undefined;
  state.observer = undefined;
}

/**
 * Joue l'attente, une seule fois.
 *
 * @param {DeferState} state
 */
function flush(state) {
  const render = state.render;
  state.render = undefined;
  disarm(state);
  if (!render) return;

  Promise.resolve()
    .then(render)
    .catch((error) => console.error(error));
}

/**
 * @param {DeferState} state
 * @param {Element} modal
 * @param {() => Promise<void>} render
 */
function defer(state, modal, render) {
  state.render = render;
  if (state.modal === modal) return;

  disarm(state);
  state.modal = modal;
  state.onClose = () => flush(state);
  modal.addEventListener('modal:close', state.onClose);
  state.observer = new MutationObserver(() => {
    if (modal.classList.contains('hidden')) flush(state);
  });
  state.observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
}

/**
 * Enveloppe le rendu d'un `turbo:before-stream-render` visant le formulaire
 * planning.
 *
 * @param {CustomEvent} event
 * @param {Document} doc
 * @param {DeferState} state l'attente partagée par l'installation
 */
export function wrapStreamRender(event, doc, state) {
  const stream = event.target;
  const detail = event.detail;
  if (!stream || !detail || typeof detail.render !== 'function') return;
  if (stream.getAttribute('action') !== 'replace') return;
  if (stream.getAttribute('target') !== PLANNING_FORM_ID) return;

  const originalRender = detail.render;
  detail.render = async (streamElement) => {
    const modal = doc.getElementById(ANNEXES_MODAL_ID);
    if (isOpen(modal)) {
      defer(state, modal, () => originalRender(streamElement));
      return;
    }

    // Plus récent qu'une attente restée armée sur une modale disparue : elle
    // ne doit jamais revenir l'écraser.
    state.render = undefined;
    disarm(state);
    await originalRender(streamElement);
  };
}

/**
 * Pose l'écouteur global, une seule fois par document.
 *
 * @param {Document} doc
 * @returns {() => void} retrait de l'écouteur (tests)
 */
export function deferPlanningFormWhileAnnexesOpen(doc = document) {
  if (doc[INSTALLED]) return doc[INSTALLED];

  /** @type {DeferState} */
  const state = {};
  const listener = (event) => wrapStreamRender(event, doc, state);
  doc.addEventListener('turbo:before-stream-render', listener);
  const teardown = () => {
    doc.removeEventListener('turbo:before-stream-render', listener);
    state.render = undefined;
    disarm(state);
    delete doc[INSTALLED];
  };
  doc[INSTALLED] = teardown;
  return teardown;
}
